import { inject, Injectable } from '@angular/core';
import { SessionClaims, SessionsServiceConfigProvider } from '../../config/sessions.config';

export interface AccessCredentials {
  httpHeaders?: { [key: string]: string };
  cookies?: { [key: string]: string };
}

export class Session {
  public constructor(
    public id: string,
    public authProvider: string,
    public authCredentials: GenericCredentials | null,
    public refreshCredentials: GenericCredentials | null,
    public prefillInfo: GenericCredentials | null,
    public accessCredentials: AccessCredentials,
    public startedAt: number,
    public sessionInfo: SessionClaims,
    public expiresAt: number | null = null,
    public refreshedAt: number | null = null,
    public terminatedAt: number | null = null,
    public redirectTo: (role: any) => string
  ) {}

  public isExpired(): boolean {
    if (this.expiresAt) {
      return this.expiresAt <= new Date().getTime();
    }

    return false;
  }
}

export type AuthProviderForm = {
  key: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'checkbox';
  saveForPrefill: boolean;
  icon?: string;
}[];

export interface AuthProvider {
  name: string;
  label: string;
  icon: string;
  supportsPasswordReset: boolean;
  redirectTo: (role: string) => string;
  getSignInFields(): AuthProviderForm;
  startSession(credentials: GenericCredentials): Promise<Session>;
  refreshSession(session: Session): Promise<Session>;

  resetPassword?(username: string): Promise<void>;
  confirmResetPassword?(username: string, confirmationCode: string, newPassword: string): Promise<void>;
  confirmNewPassword?(newPassword: string): Promise<Session>;
}

export type GenericCredentials = { [key: string]: string };

export enum SessionServiceSignal {
  SIGNIN_REQUIRED,
}

export interface SessionsServiceConfig {
  onSessionActivated?: (_: Session) => void;
  authProviders?: AuthProvider[];
  sessionDiscriminator?: (session: Session) => string;
  persistSessions?: boolean;
  autoRefreshCredentials?: boolean;
  onSelectionRequired?: () => void;
  onSignInRequired?: (session: Session) => void;
}

@Injectable({ providedIn: 'root' })
export class SessionsService {
  private authProviders: AuthProvider[];
  configProvider: SessionsServiceConfigProvider = inject(SessionsServiceConfigProvider);
  sessionDiscriminator: (session: Session) => string;
  persistSessions: boolean | undefined;
  autoRefreshCredentials: boolean;
  activeSessionId: string | null = null;
  sessions: Session[] = [];

  onSelectionRequired: () => void;
  onSignInRequired: (session: Session) => void;
  onSessionActivated: (session: Session) => void;

  public async setActiveSession(sessionId: string): Promise<void> {
    const session = this.getSession(sessionId);
    this.activeSessionId = sessionId;

    if (session.isExpired()) {
      try {
        await this.refreshSession(sessionId);
      } catch {
        this.onSignInRequired(session);
        throw new Error('User action required');
      }
    }

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    this.onSessionActivated(session);
  }

  public async getActiveSession(): Promise<Session> {
    if (!this.activeSessionId) {
      this.onSelectionRequired();
      throw new Error('No active session');
    }

    let session = this.getSession(this.activeSessionId);
    if (session.isExpired()) {
      try {
        session = await this.refreshSession(this.activeSessionId);
      } catch {
        this.onSignInRequired(session);
        throw new Error('Session expired');
      }
    }
    for (const expiredSessions of this.sessions.filter((s) => s.isExpired() && s.id !== session.id)) {
      try {
        await this.refreshSession(expiredSessions.id);
      } catch {}
    }

    return session;
  }

  public getSessions(): Session[] {
    return [...this.sessions];
  }

  public getAuthenticationProviders(): AuthProvider[] {
    return [...this.authProviders];
  }

  public setSessionClaims(sessionId: string, info: SessionClaims): SessionClaims {
    const session = this.getSession(sessionId);
    session.sessionInfo = info;
    return session.sessionInfo;
  }

  public updateSessionClaims(sessionId: string, info: Partial<SessionClaims>) {
    const session = this.getSession(sessionId);
    session.sessionInfo = {
      ...session.sessionInfo,
      ...info,
    };
    return session.sessionInfo;
  }

  public async refreshSession(sessionId: string): Promise<Session> {
    console.log('Refreshing session', sessionId);
    const session = this.getSession(sessionId);
    const authProvider = this.getAuthProvider(session.authProvider);

    let refreshedSession;

    try {
      console.log({
        message: 'Trying to refresh session',
        session: session,
      });
      refreshedSession = await authProvider.refreshSession(session);
    } catch {
      if (session.authCredentials) {
        console.log({
          message: 'Trying to restart session using saved credentials',
          session: session,
        });

        refreshedSession = await authProvider.startSession(session.authCredentials);
      } else {
        throw new Error('Could not refresh or recreate the session');
      }
    }
    const discriminator = this.sessionDiscriminator(session);

    try {
      this.sessions = this.sessions.filter((s) => s.id !== discriminator);
    } catch {}

    this.sessions.push(refreshedSession);

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    return refreshedSession;
  }

  public removeSession(sessionId: string): void {
    this.sessions = this.sessions.filter((s) => s.id !== sessionId);

    if (this.activeSessionId === sessionId) {
      this.activeSessionId = null;
    }

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    if (this.activeSessionId === null) {
      this.onSelectionRequired();
    }
  }

  public getSession(sessionId: string): Session {
    const session = this.sessions.find((s) => s.id === sessionId);
    if (!session) {
      throw new Error('Session not found');
    }
    return session;
  }

  public getAuthProvider(authProviderName: string): AuthProvider {
    const authProviderOrUndefined = this.authProviders.find((a) => a.name === authProviderName);
    if (!authProviderOrUndefined) {
      throw new Error(`Auth provider ${authProviderName} not found`);
    }
    return authProviderOrUndefined;
  }

  public async startSession(
    providerId: string,
    credentials: GenericCredentials,
    options?: {
      saveCredentials: boolean;
      setActive: boolean;
    }
  ): Promise<Session> {
    let provider = this.getAuthProvider(providerId);

    console.debug({
      message: 'Trying to start session',
      providerId: providerId,
      credentials: credentials,
    });

    let session = await provider.startSession(credentials);

    if (!(options?.saveCredentials ?? true)) {
      session.authCredentials = null;
    }

    const sessionId = this.sessionDiscriminator(session);

    try {
      this.sessions = this.sessions.filter((s) => s.id !== sessionId);
    } catch {}
    this.sessions.push(session);

    console.debug({
      message: 'Session started',
      session: session,
    });

    if (options?.setActive ?? true) {
      await this.setActiveSession(sessionId);
    }

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    return session;
  }

  public constructor() {
    const config = this.configProvider.getConfig();
    this.authProviders = config?.authProviders ?? [];
    this.persistSessions = config?.persistSessions ?? true;
    this.autoRefreshCredentials = config?.autoRefreshCredentials ?? true;
    this.onSelectionRequired = config?.onSelectionRequired ?? (() => {});
    this.onSignInRequired = config?.onSignInRequired ?? ((_) => {});
    this.onSessionActivated = config?.onSessionActivated ?? ((_) => {});

    this.sessionDiscriminator =
      config?.sessionDiscriminator ??
      ((_session) => {
        throw new Error('Session discriminator is not defined, please add it to the sessions service config');
      });

    if (this.persistSessions) {
      this.loadFromLocalStorage();
    } else {
      this.clearLocalStorage();
    }
  }

  private loadFromLocalStorage() {
    this.activeSessionId = localStorage.getItem('active_session');
    const sessionsJson = localStorage.getItem('sessions') ?? '[]';
    const parsedSessions = JSON.parse(sessionsJson) as Session[];
    this.sessions = parsedSessions.map((ps) => {
      const provider = this.authProviders.find((p) => p.name === ps.authProvider);

      const redirectTo = provider?.redirectTo ?? (() => '/');
      return new Session(
        ps.id,
        ps.authProvider,
        ps.authCredentials,
        ps.refreshCredentials,
        ps.prefillInfo,
        ps.accessCredentials,
        ps.startedAt,
        ps.sessionInfo,
        ps.expiresAt,
        ps.refreshedAt,
        ps.terminatedAt,
        redirectTo
      );
    });
  }

  private persistToLocalStorage() {
    if (this.activeSessionId) {
      localStorage.setItem('active_session', this.activeSessionId);
    } else {
      localStorage.removeItem('active_session');
    }

    const sessionsJson = JSON.stringify(this.sessions, function (_key, val) {
      if (typeof val === 'function') {
        return val + '';
      }
      return val;
    });

    localStorage.setItem('sessions', sessionsJson);
  }

  private clearLocalStorage() {
    localStorage.removeItem('sessions');
    localStorage.removeItem('active_session');
  }
}
